import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useCallback, useContext, useMemo } from 'react';
import type { FieldError } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input';
import styled from 'styled-components';

import { BasePathContext } from '@seuk/util';

import { Icon, Label } from '../../atoms';
import { InputContainer, InputHint, InputErrorIconContainer, InputErrorText } from '../text-field';

export type PhoneNumberFieldProps = React.DetailedHTMLProps<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>,
  HTMLInputElement
> & {
  id?: string;
  label?: string;
  error?: FieldError;
  value?: string;
  hint?: string;
  showErrorIcon?: boolean;
  onChange?: (value?: string) => void;
  flagUrl?: string;
};

const StyledInputContainer = styled(InputContainer)<{ error: boolean; disabled?: boolean }>`
  position: relative;

  /* Phone input custom */
  --PhoneInputCountryFlag-borderColor: black;
  --PhoneInputCountryFlag-aspectRatio: 1;
  --PhoneInputCountrySelectArrow-opacity: ${({ disabled }) => (disabled ? 0 : 1)};
  --PhoneInputCountryFlag-height: 1.5rem;

  .PhoneInputCountryIcon,
  .PhoneInputCountryIconImg {
    border-radius: 100%;
  }

  && {
    border-color: ${({ disabled }) => disabled && 'transparent'};
    background-color: ${({ disabled }) => disabled && 'transparent'};
  }

  .PhoneInput {
    width: 100%;
    padding-left: 0.75rem;
    box-sizing: border-box;
    background-color: transparent;
  }

  input.PhoneInputInput {
    ${({ theme }) => theme.typography.paragraph.medium.normal};
    padding: 0.75rem;
    border: none;
    background: none;
  }
`;

const ErrorIcon = styled(InputErrorIconContainer)`
  height: 100%;
  position: absolute;
  right: 0;
  box-sizing: border-box;
  pointer-events: none;
`;

/**
 * Phone number input component
 * IMPORTANT: When adding this component (or it's descendants) to app for the first time make sure to also add the stylesheet in the root file:
 *
 * `import 'react-phone-number-input/style.css';`
 */
export const PhoneNumberField = (props: PhoneNumberFieldProps) => {
  const { id, label, error, hint, disabled, value, onChange, showErrorIcon = true, className } = props;
  const errorId = `_input_${props.id}_error_message`;
  const internationalValue = useMemo(() => parsePhoneNumberFromString(value ?? '', 'NL')?.number, [value]);

  const onChangeHandler = useCallback(
    (newValue: string | undefined) => {
      onChange?.(newValue || '');
    },
    [onChange]
  );

  const basePath = useContext(BasePathContext);
  const flagUrl = props.flagUrl ? `${basePath}${props.flagUrl}` : `${basePath}/images/flags/{xx}.svg`;

  return (
    <div data-testid="phone-number-input" className={className}>
      {label && <Label htmlFor={id}>{label}</Label>}
      {hint && <InputHint>{hint}</InputHint>}
      <StyledInputContainer error={!!error} disabled={disabled}>
        <PhoneInput
          value={internationalValue}
          defaultCountry="GB"
          international
          countryCallingCodeEditable={false}
          id={id}
          disabled={disabled}
          aria-describedby={errorId}
          onChange={onChangeHandler}
          flagUrl={flagUrl}
        />
        {error && showErrorIcon && (
          <ErrorIcon aria-hidden="true">
            <Icon name="ErrorOutline" color="red" />
          </ErrorIcon>
        )}
      </StyledInputContainer>
      {error && (
        <InputErrorText role="alert" id={errorId}>
          {error?.message}
        </InputErrorText>
      )}
    </div>
  );
};
